import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TDownloadManager, TDownloadFile, TDownloadFileQueue, EDownloadFileStatus } from './types-download';

const initialState: TDownloadManager = {
  count: 0,
  downloadFiles: [],
  downloadFilesQueue: [],
  panelOpen: false,
};

function isValidDownloadFile(payload: TDownloadFile): boolean {
  return (
    typeof payload.id === 'string' &&
    typeof payload.fileName === 'string' &&
    Object.values(EDownloadFileStatus).includes(payload.status) &&
    typeof payload.apiFunction === 'function' &&
    payload.date instanceof Date
  );
}

function isValidDownloadFileQueue(payload: TDownloadFileQueue): boolean {
  return (
    typeof payload.id === 'string' &&
    typeof payload.fileName === 'string' &&
    Object.values(EDownloadFileStatus).includes(payload.status) &&
    typeof payload.apiQueueFunction === 'function' &&
    typeof payload.apiCheckStatusFunction === 'function' &&
    payload.date instanceof Date
  );
}

const downloadManagerDuck = createSlice({
  name: 'downloadManager',
  initialState,
  reducers: {
    closeDownloadPanel: (state) => {
      state.panelOpen = false;
    },
    addDownloadFile: (state, action: PayloadAction<TDownloadFile>) => {
      if (isValidDownloadFile(action.payload)) {
        state.downloadFiles.push(action.payload);
        state.count += 1;
        state.panelOpen = true;
      }
    },
    addDownloadFileQueue: (state, action: PayloadAction<TDownloadFileQueue>) => {
      if (isValidDownloadFileQueue(action.payload)) {
        state.downloadFilesQueue?.push(action.payload);
        state.count += 1;
        state.panelOpen = true;
      }
    },
    updateDownloadFile: (state, action: PayloadAction<TDownloadFile>) => {
      if (isValidDownloadFile(action.payload)) {
        const index = state.downloadFiles.findIndex((file) => file.id === action.payload.id);
        if (index !== -1) {
          state.downloadFiles[index] = action.payload;
        }
      }
    },
    markDownloadFileCompleted: (state, action: PayloadAction<string>) => {
      if (state.downloadFiles) {
        const file = state.downloadFiles.find((f) => f.id === action.payload);
        if (file) {
          file.status = EDownloadFileStatus.COMPLETED;
        }
      }
    },
    updateDownloadFileQueue: (state, action: PayloadAction<TDownloadFileQueue>) => {
      if (isValidDownloadFileQueue(action.payload) && state.downloadFilesQueue) {
        const index = state.downloadFilesQueue.findIndex((file) => file.id === action.payload.id);
        if (index !== -1) {
          state.downloadFilesQueue[index] = action.payload;
        }
      }
    },
    updateDownloadFileQueueStatus: (state, action: PayloadAction<{ id: string; status: EDownloadFileStatus }>) => {
      const index = state.downloadFilesQueue?.findIndex((file) => file.id === action.payload.id);
      if (state.downloadFilesQueue && index !== undefined && index !== -1) {
        state.downloadFilesQueue[index].status = action.payload.status;
      }
    },
    removeDownloadFile: (state, action: PayloadAction<string>) => {
      const initialLength = state.downloadFiles.length;
      if (state.downloadFiles) {
        state.downloadFiles = state.downloadFiles.filter((file) => file.id !== action.payload);
        if (state.downloadFiles.length < initialLength) {
          state.count -= 1;
        }
      }
    },
    markDownloadQueueFileCompleted: (state, action: PayloadAction<string>) => {
      if (state.downloadFilesQueue) {
        const file = state.downloadFilesQueue.find((f) => f.id === action.payload);
        if (file) {
          file.status = EDownloadFileStatus.COMPLETED;
        }
      }
    },
    removeDownloadFileQueue: (state, action: PayloadAction<string>) => {
      if (state.downloadFilesQueue) {
        const initialLength = state.downloadFilesQueue.length;
        state.downloadFilesQueue = state.downloadFilesQueue.filter((file) => file.id !== action.payload);
        if (state.downloadFilesQueue.length < initialLength) {
          state.count -= 1;
        }
      }
    },
    removeDownloadFileEverywhere: (state, action: PayloadAction<string[]>) => {
      // Remove from downloadFiles
      if (state.downloadFiles) {
        state.downloadFiles = state.downloadFiles.filter((file) => !action.payload.includes(file.id));
      }
      // Remove from downloadFilesQueue
      if (state.downloadFilesQueue) {
        state.downloadFilesQueue = state.downloadFilesQueue.filter((file) => !action.payload.includes(file.id));
      }
      state.count = Math.max(0, state.downloadFiles.length + (state.downloadFilesQueue ? state.downloadFilesQueue.length : 0));
    },
    cleanDownloadManager: (state) => {
      state.count = 0;
      state.downloadFiles = [];
      state.downloadFilesQueue = [];
      state.panelOpen = false;
    },
  },
});
export const {
  closeDownloadPanel,
  addDownloadFile,
  addDownloadFileQueue,
  updateDownloadFile,
  removeDownloadFile,
  removeDownloadFileQueue,
  removeDownloadFileEverywhere,
  markDownloadQueueFileCompleted,
  markDownloadFileCompleted,
  updateDownloadFileQueueStatus,
  cleanDownloadManager,
} = downloadManagerDuck.actions;
export default downloadManagerDuck.reducer;
